import { useEffect, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { wibNow } from "@/lib/time";

const MON = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const WEEKDAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat"];

function mondayOf(d) {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  x.setHours(0, 0, 0, 0);
  return x;
}
const dstr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function cellStyle(rec, dateStr, todayStr) {
  if (rec && rec.status === "Izin") return { cls: "bg-stone-200 text-stone-500", txt: "Izin" };
  if (rec && rec.time_in) {
    const [h, m] = rec.time_in.split(".").map(Number);
    const mins = h * 60 + m;
    const t = rec.time_in.replace(".", ":");
    if (mins <= 7 * 60 + 10) return { cls: "bg-green-100 text-green-700", txt: t };
    if (mins <= 7 * 60 + 30) return { cls: "bg-yellow-100 text-yellow-700", txt: t };
    return { cls: "bg-red-100 text-red-600", txt: t };
  }
  if (dateStr > todayStr) return { cls: "bg-white text-stone-300", txt: "" };
  return { cls: "bg-stone-100 text-stone-400", txt: "-" };
}

export function WeeklyHoursCard() {
  const [recs, setRecs] = useState([]);
  const [piket, setPiket] = useState({});
  const [weekOffset, setWeekOffset] = useState(0);
  useEffect(() => {
    api.get("/teacher/attendance/me").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/teacher/piket/mine").then((r) => setPiket(r.data)).catch(() => {});
  }, []);
  const recsMap = {};
  recs.forEach((r) => { recsMap[r.date] = r; });
  const weekStart = mondayOf(wibNow());
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekDates = [0, 1, 2, 3, 4].map((i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const todayStr = dstr(wibNow());
  const rangeLabel = `${weekDates[0].getDate()} ${MON[weekDates[0].getMonth()]} – ${weekDates[4].getDate()} ${MON[weekDates[4].getMonth()]} ${weekDates[4].getFullYear()}`;

  return (
    <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4" data-testid="weekly-hours-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-stone-800 text-sm">Jam Masuk per Hari</h3>
        <div className="flex items-center gap-1.5">
          <button data-testid="wh-week-prev" onClick={() => setWeekOffset((w) => w - 1)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center active:scale-90 transition-transform"><CaretLeft size={16} weight="bold" /></button>
          <button data-testid="wh-week-next" onClick={() => setWeekOffset((w) => Math.min(0, w + 1))} disabled={weekOffset >= 0}
            className={`w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform ${weekOffset >= 0 ? "bg-stone-50 text-stone-300" : "bg-stone-100"}`}><CaretRight size={16} weight="bold" /></button>
        </div>
      </div>
      <p className="text-xs text-stone-500 mb-3" data-testid="wh-week-range">{rangeLabel}{weekOffset === 0 ? " (minggu ini)" : ""}</p>
      <div className="overflow-x-auto no-scrollbar -mx-1">
        <table className="w-full border-collapse text-xs rounded-xl overflow-hidden" data-testid="weekly-hours-table">
          <thead>
            <tr className="bg-teal-800 text-white">
              {weekDates.map((d, i) => (
                <th key={i} className="font-bold px-2 py-2 text-center min-w-[64px]">{WEEKDAYS[i]}<br /><span className="text-teal-100 font-semibold">{d.getDate()}/{d.getMonth() + 1}</span></th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-teal-50/60">
              {weekDates.map((d, i) => {
                const ds = dstr(d);
                const st = cellStyle(recsMap[ds], ds, todayStr);
                const pk = piket[ds];
                return <td key={i} className="px-1 py-1.5 text-center border-t border-teal-100"><span data-testid={pk ? `wh-piket-${ds}` : undefined} title={pk ? (pk === "tepat_waktu" ? "Piket tepat waktu" : "Piket terlambat") : undefined} className={`inline-block w-full py-1.5 font-bold ${st.cls} ${pk ? "rounded-full ring-[3px] ring-orange-500 ring-offset-1" : "rounded-lg"}`}>{st.txt}</span></td>;
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block" /> Tepat waktu (≤07:10)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 inline-block" /> 07:11–07:30</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block" /> Terlambat (&gt;07:30)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-stone-200 inline-block" /> Izin / Tidak hadir</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full ring-[3px] ring-orange-500 inline-block" /> Piket pagi</span>
      </div>
    </div>
  );
}
